/**
 * Timezone Utility Functions for DeeTrade
 * 
 * Handles conversion between UTC (server time) and user's local timezone.
 * All server times are stored in UTC/GMT for consistency.
 * 
 * @module utils/timezone
 */

/**
 * Convert UTC time string to user's local timezone
 * @param {string} utcTimeString - ISO 8601 UTC time string (e.g., "2025-11-25T10:30:00+00:00")
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted local time string
 */
export const utcToLocal = (utcTimeString, options = {}) => {
  if (!utcTimeString) return 'N/A';
  
  try {
    const utcDate = new Date(utcTimeString);
    
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
      ...options
    };
    
    return new Intl.DateTimeFormat(undefined, defaultOptions).format(utcDate);
  } catch (error) {
    console.error('Error converting UTC to local time:', error);
    return utcTimeString;
  }
};

/**
 * Convert local time to UTC
 * @param {Date} localDate - Local Date object
 * @returns {string} ISO 8601 UTC time string
 */
export const localToUtc = (localDate) => {
  if (!localDate) return null;
  
  try {
    return localDate.toISOString();
  } catch (error) {
    console.error('Error converting local to UTC:', error);
    return null;
  }
};

/**
 * Get user's timezone name
 * @returns {string} Timezone name (e.g., "America/New_York", "Asia/Kolkata")
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'UTC';
  }
};

/**
 * Get user's timezone offset from UTC in hours
 * @returns {number} Offset in hours (e.g., -5 for EST, 5.5 for IST)
 */
export const getTimezoneOffset = () => {
  const offsetMinutes = new Date().getTimezoneOffset();
  return -offsetMinutes / 60; // Negative because getTimezoneOffset returns opposite sign
};

/**
 * Format time for display with both local and UTC
 * @param {string} utcTimeString - ISO 8601 UTC time string
 * @returns {object} Object with local and utc formatted strings
 */
export const formatTimeWithBothTimezones = (utcTimeString) => {
  if (!utcTimeString) {
    return { local: 'N/A', utc: 'N/A' };
  }
  
  try {
    const utcDate = new Date(utcTimeString);
    
    const localFormatted = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }).format(utcDate);
    
    const utcFormatted = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    }).format(utcDate) + ' UTC';
    
    return { local: localFormatted, utc: utcFormatted };
  } catch (error) {
    console.error('Error formatting time:', error);
    return { local: utcTimeString, utc: utcTimeString };
  }
};

/**
 * Format schedule time for display
 * Shows hour:minute in user's local time with UTC equivalent
 * @param {number} utcHour - Hour in UTC (0-23)
 * @param {number} utcMinute - Minute (0-59)
 * @returns {object} Object with local time, utc time, and timezone info
 */
export const formatScheduleTime = (utcHour, utcMinute = 0) => {
  if (utcHour === undefined || utcHour === '*') {
    return { local: 'Every hour', utc: 'Every hour', timezone: getUserTimezone() };
  }
  
  try {
    // Create a date with the UTC time
    const now = new Date();
    const utcDate = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      utcHour,
      utcMinute,
      0
    ));
    
    // Format in local time
    const localTime = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(utcDate);
    
    // Format in UTC
    const utcTime = `${String(utcHour).padStart(2, '0')}:${String(utcMinute).padStart(2, '0')} UTC`;
    
    return {
      local: localTime,
      utc: utcTime,
      timezone: getUserTimezone()
    };
  } catch (error) {
    console.error('Error formatting schedule time:', error);
    return {
      local: `${utcHour}:${utcMinute}`,
      utc: `${utcHour}:${utcMinute} UTC`,
      timezone: 'UTC'
    };
  }
};

/**
 * Convert local hour/minute to UTC for schedule updates
 * @param {number} localHour - Hour in local time (0-23)
 * @param {number} localMinute - Minute (0-59)
 * @returns {object} Object with utcHour and utcMinute
 */
export const localTimeToUtc = (localHour, localMinute = 0) => {
  try {
    // Create a date with local time
    const now = new Date();
    const localDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      localHour,
      localMinute,
      0
    );
    
    return {
      utcHour: localDate.getUTCHours(),
      utcMinute: localDate.getUTCMinutes()
    };
  } catch (error) {
    console.error('Error converting local time to UTC:', error);
    return { utcHour: localHour, utcMinute: localMinute };
  }
};

/**
 * Format day of week
 * @param {number|string} dayOfWeek - Day of week (0-6, Sunday=0, or '*' for all)
 * @returns {string} Formatted day name
 */
export const formatDayOfWeek = (dayOfWeek) => {
  if (dayOfWeek === '*' || dayOfWeek === undefined) {
    return 'Every day';
  }
  
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayNum = typeof dayOfWeek === 'string' ? parseInt(dayOfWeek, 10) : dayOfWeek;
  
  return days[dayNum] || 'Unknown';
};

/**
 * Calculate relative time from now
 * @param {string} utcTimeString - ISO 8601 UTC time string
 * @returns {string} Relative time string (e.g., "in 2 hours", "3 days ago")
 */
export const getRelativeTime = (utcTimeString) => {
  if (!utcTimeString) return 'N/A';
  
  try {
    const targetDate = new Date(utcTimeString);
    const now = new Date();
    const diffMs = targetDate - now;
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    if (Math.abs(diffMinutes) < 60) {
      return diffMinutes > 0 ? `in ${diffMinutes} minutes` : `${Math.abs(diffMinutes)} minutes ago`;
    } else if (Math.abs(diffHours) < 24) {
      return diffHours > 0 ? `in ${diffHours} hours` : `${Math.abs(diffHours)} hours ago`;
    } else {
      return diffDays > 0 ? `in ${diffDays} days` : `${Math.abs(diffDays)} days ago`;
    }
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return utcTimeString;
  }
};

/**
 * Get current UTC time string
 * @returns {string} Current time in ISO 8601 UTC format
 */
export const getCurrentUtcTime = () => {
  return new Date().toISOString();
};

// Export all functions as named exports for consistency
// (already exported individually above)

// =============================================================================
// EXCHANGE TIMEZONE UTILITIES
// These functions handle exchange-specific timezone operations.
// Exchange time is canonical for all market data operations.
// =============================================================================

// Cache for exchange metadata (avoid repeated API calls)
const exchangeMetadataCache = {};

// Fallback timezone map (used when API is unavailable)
const FALLBACK_EXCHANGE_TIMEZONES = {
  NSE: 'Asia/Kolkata',
  NFO: 'Asia/Kolkata',
  BSE: 'Asia/Kolkata',
  BFO: 'Asia/Kolkata',
  MCX: 'Asia/Kolkata',
  CDS: 'Asia/Kolkata',
  NYSE: 'America/New_York',
  NASDAQ: 'America/New_York',
  LSE: 'Europe/London',
};

/**
 * Get timezone for an exchange (fetches from backend API, with fallback)
 * @param {string} exchange - Exchange code (e.g., 'NSE', 'MCX')
 * @param {object} api - Optional API instance for fetching (defaults to window.api if available)
 * @returns {Promise<string>} Timezone string (e.g., 'Asia/Kolkata')
 */
export const getExchangeTimezone = async (exchange, api = null) => {
  const key = exchange?.toUpperCase();
  
  // Check cache first
  if (exchangeMetadataCache[key]?.timezone) {
    return exchangeMetadataCache[key].timezone;
  }
  
  // Try to fetch from backend
  try {
    const apiInstance = api || (typeof window !== 'undefined' ? window.api : null);
    if (apiInstance) {
      const response = await apiInstance.get(`/calendar/exchange-hours/${key}`);
      if (response?.data?.timezone) {
        exchangeMetadataCache[key] = response.data;
        return response.data.timezone;
      }
    }
  } catch (error) {
    console.warn(`Failed to fetch timezone for ${key}, using fallback:`, error?.message);
  }
  
  // Fallback
  return FALLBACK_EXCHANGE_TIMEZONES[key] || 'Asia/Kolkata';
};

/**
 * Get timezone synchronously (uses cache or fallback, doesn't fetch)
 * Use this when you need immediate result without async/await
 * @param {string} exchange - Exchange code
 * @returns {string} Timezone string
 */
export const getExchangeTimezoneSync = (exchange) => {
  const key = exchange?.toUpperCase();
  return exchangeMetadataCache[key]?.timezone || FALLBACK_EXCHANGE_TIMEZONES[key] || 'Asia/Kolkata';
};

/**
 * Convert exchange time to user's local time for display
 * @param {string} timeStr - Time string in exchange timezone (HH:MM format)
 * @param {string} exchangeTimezone - Exchange timezone (e.g., 'Asia/Kolkata')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time in user's local timezone
 */
export const exchangeToLocal = (timeStr, exchangeTimezone, options = {}) => {
  if (!timeStr) return 'N/A';
  
  try {
    // Parse HH:MM or HH:MM:SS format
    const [hours, minutes, seconds = 0] = timeStr.split(':').map(Number);
    
    // Create a date string for today in the exchange timezone
    // We'll use a specific date to construct the time in the exchange TZ
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const timeFormatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    
    // Create a date string and parse it in the exchange timezone context
    // We format the date in exchange timezone, then reinterpret in local
    const exchangeDateStr = `${year}-${month}-${day}T${timeFormatted}`;
    
    // Parse as if it's in exchange timezone by using the timezone offset
    const dateInExchangeTz = new Date(exchangeDateStr);
    
    // Get the time difference between exchange timezone and UTC
    const exchangeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: exchangeTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Get UTC time by creating a date that would show as the given time in exchange TZ
    // This is tricky - we need to find the UTC moment when it's the given time in exchange TZ
    const parts = exchangeFormatter.formatToParts(new Date());
    const nowInExchangeTz = {
      year: parseInt(parts.find(p => p.type === 'year').value),
      month: parseInt(parts.find(p => p.type === 'month').value) - 1,
      day: parseInt(parts.find(p => p.type === 'day').value),
      hour: parseInt(parts.find(p => p.type === 'hour').value),
      minute: parseInt(parts.find(p => p.type === 'minute').value)
    };
    
    // Calculate the offset in minutes between exchange time and local time
    const nowLocal = new Date();
    const localMinutes = nowLocal.getHours() * 60 + nowLocal.getMinutes();
    const exchangeMinutes = nowInExchangeTz.hour * 60 + nowInExchangeTz.minute;
    const offsetMinutes = exchangeMinutes - localMinutes;
    
    // Convert exchange time to local time by subtracting the offset
    const exchangeTimeMinutes = hours * 60 + minutes;
    const localTimeMinutes = exchangeTimeMinutes - offsetMinutes;
    
    // Handle day wraparound
    let localHours = Math.floor(localTimeMinutes / 60);
    let localMins = localTimeMinutes % 60;
    
    if (localHours < 0) localHours += 24;
    if (localHours >= 24) localHours -= 24;
    if (localMins < 0) localMins += 60;
    
    // Format the local time
    const defaultOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    // Create a date object with the local time for formatting
    const localDate = new Date();
    localDate.setHours(localHours, localMins, 0, 0);
    
    // Remove timeZoneName from options to avoid duplication
    const { timeZoneName, ...formatOptions } = defaultOptions;
    
    return new Intl.DateTimeFormat(undefined, formatOptions).format(localDate);
  } catch (error) {
    console.error('Error converting exchange to local time:', error);
    return timeStr;
  }
};

/**
 * Format time showing both exchange and local timezone
 * @param {string} timeStr - Time string
 * @param {string} exchangeTimezone - Exchange timezone
 * @returns {object} { exchange: string, local: string }
 */
export const formatTimeWithExchangeAndLocal = (timeStr, exchangeTimezone) => {
  if (!timeStr) {
    return { exchange: 'N/A', local: 'N/A' };
  }
  
  try {
    const date = new Date(timeStr);
    
    // Format in exchange timezone
    const exchangeFormatted = new Intl.DateTimeFormat('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: exchangeTimezone
    }).format(date) + ' ' + exchangeTimezone.split('/').pop();
    
    // Format in user's local timezone
    const localFormatted = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }).format(date);
    
    return { exchange: exchangeFormatted, local: localFormatted };
  } catch (error) {
    console.error('Error formatting time with both timezones:', error);
    return { exchange: timeStr, local: timeStr };
  }
};

// =============================================================================
// EXCHANGE-AWARE DATE HELPERS (for charts, backtesting, period analysis)
// =============================================================================

// Cached Intl.DateTimeFormat instances per timezone (avoid re-creation)
const _datePartsFormatters = {};

/**
 * Extract date parts (year, month, day, weekday) in a specific timezone.
 * Uses Intl.DateTimeFormat for DST-correct conversion.
 *
 * @param {string|number|Date} timestamp - ISO string, Unix ms, or Date object
 * @param {string} timezone - IANA timezone (e.g., 'Asia/Kolkata')
 * @returns {{ year: number, month: number, day: number, weekday: number, hour: number, minute: number }}
 */
export const getDatePartsInTimezone = (timestamp, timezone = 'Asia/Kolkata') => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return null;

  const key = timezone;
  if (!_datePartsFormatters[key]) {
    _datePartsFormatters[key] = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  const parts = _datePartsFormatters[key].formatToParts(date);
  const get = (type) => parts.find(p => p.type === type)?.value;
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  return {
    year: parseInt(get('year'), 10),
    month: parseInt(get('month'), 10) - 1, // 0-indexed like JS Date
    day: parseInt(get('day'), 10),
    weekday: weekdayMap[get('weekday')] ?? 0,
    hour: parseInt(get('hour'), 10),
    minute: parseInt(get('minute'), 10),
  };
};

/**
 * Format a timestamp as YYYY-MM-DD in a specific timezone.
 * Used for date range strings, API calls, and display.
 *
 * @param {string|number|Date} timestamp - ISO string, Unix ms, or Date object
 * @param {string} timezone - IANA timezone (e.g., 'Asia/Kolkata')
 * @returns {string} 'YYYY-MM-DD'
 */
export const formatDateInTimezone = (timestamp, timezone = 'Asia/Kolkata') => {
  const p = getDatePartsInTimezone(timestamp, timezone);
  if (!p) return '';
  return `${p.year}-${String(p.month + 1).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
};

/**
 * Compute the UTC offset (in seconds) for a given exchange timezone
 * at a specific instant. Accounts for DST where applicable.
 *
 * @param {string} timezone - IANA timezone string
 * @param {number} [atUnixMs] - Unix milliseconds to evaluate the offset at (default: now)
 * @returns {number} Offset in seconds (positive east of UTC, e.g. +19800 for IST)
 */
export const getExchangeOffsetSeconds = (timezone, atUnixMs = Date.now()) => {
  if (!timezone) return 0;
  // Intl trick: compare the "wall clock" in the target timezone vs UTC
  // Use hourCycle:'h23' (0-23) to avoid en-US h24 cycle where midnight = 24
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, hourCycle: 'h23',
    hour: 'numeric', minute: 'numeric', year: 'numeric', month: 'numeric', day: 'numeric',
  });
  const utfDtf = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', hourCycle: 'h23',
    hour: 'numeric', minute: 'numeric', year: 'numeric', month: 'numeric', day: 'numeric',
  });
  const d = new Date(atUnixMs);
  const localParts = dtf.formatToParts(d);
  const utcParts = utfDtf.formatToParts(d);
  const g = (parts, type) => parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);
  const localMin = g(localParts, 'hour') * 60 + g(localParts, 'minute');
  const utcMin = g(utcParts, 'hour') * 60 + g(utcParts, 'minute');
  const localDay = g(localParts, 'day');
  const utcDay = g(utcParts, 'day');
  let diffMin = (localMin - utcMin) + (localDay - utcDay) * 1440;
  // Handle month boundary wrap (e.g. local day=1, UTC day=31)
  if (diffMin > 720) diffMin -= 1440;
  if (diffMin < -720) diffMin += 1440;
  return diffMin * 60;
};
